import type { BtnOptions, Decorations } from '@Types/Shared/Button';

import { useRef, useState } from 'react';

import parse from 'html-react-parser';

import apiFetch from '@wordpress/api-fetch';

import type { GForm, Page } from '@/src/Types/GF/form';
import { router } from '@inertiajs/react';
import { ValidationIcon } from '@Views/Shared/Icons';

import SubmitBtn from '../../_components/Submit';
import { getSubmitBtnSettingsToCSS } from '../../_lib/btns';
import GravityFormsField from '../GF/GravityFormsField';

export default function GForm({
	form,
	form_id,
	submitBtn,
}: {
	form?: GForm;
	form_id?: string;
	btnHasIcon?: boolean;
	btnDecorationElement?: Decorations;
	submitBtn?: BtnOptions;
}) {
	const [confirmationMessage, setConfirmationMessage] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState<any>('');
	const [isLoaded, setLoaded] = useState(true);

	const myForm = useRef<HTMLFormElement>(null);

	const [isValid, setIsValid] = useState<boolean>(false);
	const [formValues, setFormValues] = useState<any>({
		form_id: form_id,
		values: {},
	});

	const getConfirmations = () => {
		if (form?.confirmations) {
			const confirmations = form?.confirmations[Object.keys(form?.confirmations)[0]];

			if (confirmations.type === 'page' && confirmations.pageId) {
				return apiFetch<Page>({
					path: `/wp/v2/pages/${confirmations.pageId}`,
				}).then((page) => {
					router.visit(page.link, {
						method: 'get',
					});
				});
			}

			if (confirmations.type === 'redirect' && confirmations.url) {
				window.location.href = confirmations.url;
			}
		}
	};

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		SubmitForm();
	}

	function SubmitForm() {
		setLoaded(false);
		//see REST API docs https://docs.gravityforms.com/submitting-forms-with-rest-api-v2/

		myForm.current?.scrollIntoView({
			behavior: 'smooth',
		});

		const _posts: Promise<string> = apiFetch({
			path: `/gf/v2/forms/${form_id}/submissions`,
			method: 'POST',
			data: formValues.values,
		});

		_posts.then((req: any) => {
			if (req.is_valid) {
				setIsValid(true);
				setConfirmationMessage(req.confirmation_message);
			}

			setLoaded(true);
		});

		_posts.catch(async (req: any) => {
			if (Object.prototype.hasOwnProperty.call(req, 'is_valid')) {
				setIsValid(false);
				setErrorMessage(req.validation_messages);
			} else {
				getConfirmations();
			}

			setLoaded(true);
		});
	}

	return (
		<>
			{isValid ? (
				<div className='font-minos-form-content-font-family text-center text-base text-minos-form-content-color tablet:minos-form-content-font-style'>
					<ValidationIcon className='mx-auto mb-4 block w-fit' />
					{parse(confirmationMessage)}
				</div>
			) : (
				<>
					{!isLoaded && (
						<div className='flex justify-center p-10'>
							<div className='size-10 animate-spin rounded-full border-4 border-gray-300 border-t-black p-5' />
						</div>
					)}

					<form
						ref={myForm}
						method='post'
						onSubmit={handleSubmit}
						className={`${!isLoaded && 'pointer-events-none opacity-40'}`}
					>
						{form?.fields && (
							<div className='minos-form-content-font-style text-base tablet:minos-form-content-font-style'>
								{form?.fields.map((field) => {
									return (
										<GravityFormsField
											key={field?.id}
											field={field}
											fields={form?.fields}
											errorMessage={errorMessage}
											setFormValues={setFormValues}
											formValues={formValues}
										/>
									);
								})}
							</div>
						)}

						<SubmitBtn
							textLabel={form?.button?.text || 'Submit'}
							submitStyle={getSubmitBtnSettingsToCSS(submitBtn, 'form')}
							className='group-hover:text-[color:var(--m-form-btn-submit-label-color-hover)]'
						/>
					</form>
				</>
			)}
		</>
	);
}
